import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageSquare, MapPin, Instagram } from "lucide-react";
import Logo from "../components/Logo";
import { SectionLabel, SectionTitle } from "../components/Section";
import { services } from "../data/catalog";
import { CONTACT } from "../data/contact";

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[88vh] flex flex-col items-center justify-center px-5 sm:px-8 md:px-12 pt-10 pb-20 border-b border-gray-900 overflow-hidden"
      >
        {/* Faint grid pattern */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <p
          data-testid="hero-eyebrow"
          className="reveal text-[10px] sm:text-xs font-display font-light uppercase tracking-[0.4em] sm:tracking-[0.5em] text-gray-500 mb-10 text-center"
        >
          Mobile Auto Detailing — Philadelphia
        </p>

        <Logo size="xl" testId="hero-logo" className="reveal" />

        <div className="hairline w-56 sm:w-80 mt-10 mb-8" />

        <p
          data-testid="hero-tagline"
          className="reveal text-xs sm:text-sm font-light tracking-[0.2em] uppercase text-gray-400 text-center max-w-md sm:max-w-2xl px-4"
        >
          Shop-quality detailing<br className="sm:hidden" />
          <span className="hidden sm:inline"> · </span>Brought to your driveway
        </p>

        <div className="reveal mt-12 flex flex-col w-full sm:w-auto sm:flex-row gap-3 sm:gap-5 max-w-xs sm:max-w-none">
          <Link
            to="/contact"
            data-testid="hero-cta-quote"
            className="w-full sm:w-auto px-10 py-4 border border-gray-200 text-[11px] font-display font-light uppercase tracking-[0.3em] text-center text-white hover:bg-white hover:text-black transition-all duration-300"
          >
            Request a Quote
          </Link>
          <a
            href={CONTACT.smsHref}
            data-testid="hero-cta-call"
            className="w-full sm:w-auto px-10 py-4 border border-gray-800 text-[11px] font-display font-light uppercase tracking-[0.3em] text-center text-gray-300 hover:text-white hover:border-gray-500 transition-all duration-300 inline-flex items-center justify-center gap-2"
          >
            <MessageSquare strokeWidth={1} size={14} />
            Text {CONTACT.phone}
          </a>
        </div>

        <div className="reveal mt-16 flex flex-col items-center gap-3">
          <div className="flex items-center gap-2 text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500">
            <MapPin strokeWidth={1} size={12} />
            {CONTACT.serviceArea}
          </div>
          <a
            href={CONTACT.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-instagram"
            className="flex items-center gap-2 text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 hover:text-white transition-colors"
          >
            <Instagram strokeWidth={1} size={12} />
            {CONTACT.instagram}
          </a>
        </div>
      </section>

      {/* INTRO */}
      <section
        data-testid="intro-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-28 border-b border-gray-900"
      >
        <div className="max-w-3xl mx-auto text-center">
          <SectionLabel testId="intro-label">About SPINLAB</SectionLabel>
          <h2 className="font-display font-extralight uppercase tracking-[0.18em] text-white text-2xl sm:text-4xl lg:text-5xl leading-[1.2]">
            Shop-quality detailing,<br />brought directly to your driveway.
          </h2>
          <p className="mt-10 text-sm sm:text-base font-light tracking-wide text-gray-400 leading-relaxed">
            SPINLAB is a one-person, owner-operated mobile detailing business
            based in {CONTACT.city}. I personally handle every vehicle from
            start to finish and bring a fully equipped detailing setup directly
            to your driveway.
          </p>
          <p className="mt-5 text-sm sm:text-base font-light tracking-wide text-gray-400 leading-relaxed">
            Professional mobile detailing that saves you time without
            compromising the quality of the work.
          </p>
          <Link
            to="/about"
            data-testid="intro-cta"
            className="mt-8 inline-flex items-center gap-3 text-xs font-display font-light uppercase tracking-[0.3em] text-gray-300 hover:text-white transition-colors group"
          >
            More about me
            <ArrowUpRight strokeWidth={1} size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section
        data-testid="services-preview-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-28 border-b border-gray-900"
      >
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <SectionLabel testId="services-preview-label">Services</SectionLabel>
            <SectionTitle testId="services-preview-title" className="max-w-3xl">
              Three core packages.
            </SectionTitle>
          </div>
          <Link
            to="/services"
            data-testid="services-preview-link"
            className="group inline-flex items-center gap-3 text-xs font-display font-light uppercase tracking-[0.3em] text-gray-400 hover:text-white transition-colors"
          >
            All services
            <ArrowUpRight strokeWidth={1} size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>

        <div className="border-t border-gray-900">
          {services.map((s, i) => (
            <Link
              key={s.slug}
              to="/services"
              data-testid={`services-preview-row-${s.slug}`}
              className="group flex items-start sm:items-center justify-between gap-4 sm:gap-6 py-7 md:py-10 border-b border-gray-900 hover:bg-[#080808] transition-colors px-1 sm:px-4"
            >
              <div className="flex items-start sm:items-center gap-4 sm:gap-8 min-w-0">
                <span className="text-[10px] font-display font-light tracking-[0.3em] text-gray-600 pt-1 sm:pt-0 shrink-0">
                  0{i + 1}
                </span>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-2xl font-display font-extralight uppercase tracking-[0.18em] text-white">
                    {s.name}
                  </h3>
                  <p className="mt-2 text-xs sm:text-sm font-light text-gray-500 line-clamp-2 sm:line-clamp-none">{s.summary}</p>
                </div>
              </div>
              <div className="flex flex-col items-end shrink-0">
                <span className="text-[9px] sm:text-[10px] font-display font-light uppercase tracking-[0.3em] text-gray-600">Starting at</span>
                <span className="text-lg sm:text-2xl font-display font-extralight text-white mt-0.5 sm:mt-1">${s.pricing[0].price}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SERVICE AREA */}
      <section
        data-testid="service-area-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-28 border-b border-gray-900"
      >
        <div className="max-w-3xl">
          <SectionLabel testId="service-area-label">Service Area</SectionLabel>
          <h2 className="font-display font-extralight uppercase tracking-[0.18em] text-white text-2xl sm:text-4xl lg:text-5xl leading-[1.2]">
            Mobile detailing throughout<br />Philadelphia and surrounding areas.
          </h2>
          <p className="mt-8 text-sm sm:text-base font-light text-gray-400 leading-relaxed max-w-xl">
            Not sure if you&apos;re in range?{" "}
            <a
              href={CONTACT.smsHref}
              data-testid="service-area-sms"
              className="text-white underline underline-offset-4 decoration-gray-600 hover:decoration-white transition-colors"
            >
              Send me your ZIP code
            </a>
            .
          </p>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section
        data-testid="home-instagram-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-20 md:py-28 border-b border-gray-900 text-center"
      >
        <SectionLabel testId="home-instagram-label">See the work</SectionLabel>
        <h2 className="font-display font-extralight uppercase tracking-[0.18em] text-white text-2xl sm:text-4xl lg:text-5xl leading-[1.2] max-w-3xl mx-auto">
          Recent details on Instagram.
        </h2>
        <p className="mt-8 text-sm sm:text-base font-light text-gray-400 leading-relaxed max-w-xl mx-auto">
          Every new vehicle, behind-the-scenes step and finished result lives on my Instagram.
        </p>
        <a
          href={CONTACT.instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="home-instagram-cta"
          className="mt-12 inline-flex items-center gap-3 px-10 sm:px-12 py-5 border border-gray-200 text-[11px] font-display font-light uppercase tracking-[0.3em] text-white hover:bg-white hover:text-black transition-all"
        >
          <Instagram strokeWidth={1} size={16} />
          {CONTACT.instagram}
        </a>
      </section>

      {/* CTA */}
      <section
        data-testid="home-cta-section"
        className="px-5 sm:px-8 md:px-12 lg:px-20 py-24 md:py-32 text-center"
      >
        <SectionLabel testId="home-cta-label">Ready when you are</SectionLabel>
        <h2 className="font-display font-extralight uppercase tracking-[0.18em] text-white text-2xl sm:text-4xl lg:text-5xl leading-[1.2] max-w-3xl mx-auto">
          Book a detail.<br />Get your vehicle back<br className="sm:hidden" /> properly cleaned.
        </h2>
        <div className="mt-12 flex flex-col items-center gap-4">
          <Link
            to="/contact"
            data-testid="home-cta-btn"
            className="w-full max-w-xs sm:w-auto sm:max-w-none px-12 py-5 border border-gray-200 text-[11px] font-display font-light uppercase tracking-[0.3em] text-white hover:bg-white hover:text-black transition-all duration-300"
          >
            Request a Quote
          </Link>
          <a
            href={CONTACT.smsHref}
            data-testid="home-cta-call"
            className="inline-flex items-center gap-2 text-xs font-display font-light uppercase tracking-[0.3em] text-gray-400 hover:text-white transition-colors"
          >
            <MessageSquare strokeWidth={1} size={14} /> Or text {CONTACT.phone}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
